/**
 * Converts a File object to a base64 encoded string with client-side compression.
 * Resizes images to max 1024px to reduce token usage and upload latency.
 */
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const MAX_SIZE = 1024;

                // Calculate new dimensions
                if (width > height) {
                    if (width > MAX_SIZE) {
                        height *= MAX_SIZE / width;
                        width = MAX_SIZE;
                    }
                } else {
                    if (height > MAX_SIZE) {
                        width *= MAX_SIZE / height;
                        height = MAX_SIZE;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error("Canvas context failed"));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);
                
                // Compress to JPEG at 80% quality
                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                resolve(dataUrl.split(',')[1]);
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};

/**
 * Adds a subtle, semi-transparent watermark to a base64 image.
 * Waits for fonts to load to ensure correct rendering.
 */
export const addWatermarkToImage = (base64Data: string): Promise<string> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = async () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
                resolve(base64Data);
                return;
            }
            
            // Draw original image
            ctx.drawImage(img, 0, 0);
            
            // Wait for fonts to be ready to prevent fallback font rendering (Times New Roman)
            if (document.fonts) {
                await document.fonts.ready;
            }

            // Configure watermark style
            const fontSize = Math.max(14, Math.floor(canvas.width * 0.025));
            const padding = Math.floor(canvas.width * 0.03);
            
            ctx.font = `600 ${fontSize}px 'Poppins', sans-serif`;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';
            
            ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            ctx.shadowBlur = 4;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;

            ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
            ctx.fillText('Najeweller AI', canvas.width - padding, canvas.height - padding);
            
            resolve(canvas.toDataURL('image/png', 0.92));
        };
        img.onerror = () => {
            console.error("Failed to load image for watermarking");
            resolve(base64Data);
        };
        img.src = base64Data;
    });
};